import { useState } from "react";
import { toast } from "sonner";
import { Wrench } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Eyebrow, FadeUp, MaskLine } from "./Reveal";

const SERVICES = ["Repair", "Upgrade", "Both"];
const empty = { name: "", email: "", phone: "", brand: "", model: "", service: "Repair", details: "", budget: "" };

const Field = ({ label, children }) => (
  <label className="block"><span className="field-label">{label} *</span>{children}</label>
);

export const Workshop = () => {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/repairs", form);
      toast.success(t("ws.ok"));
      setForm(empty);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="workshop" data-testid="workshop-section" className="px-5 sm:px-8 lg:px-12 py-24 sm:py-36 border-b border-[var(--line)] grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <Eyebrow>{t("ws.eyebrow")}</Eyebrow>
        <h2 className="font-display font-black uppercase text-5xl sm:text-7xl leading-[0.85] mt-5">
          <MaskLine>{t("ws.title")[0]}</MaskLine>
          <MaskLine delay={0.1}>{t("ws.title")[1]}</MaskLine>
        </h2>
        <FadeUp className="mt-8 text-sm sm:text-base text-[var(--muted)] leading-relaxed max-w-md">{t("ws.intro")}</FadeUp>
        <FadeUp className="mt-10">
          <div className="group relative clip-cut overflow-hidden aspect-[4/3]">
            <img data-testid="workshop-image" src={useSite().img("workshop")} alt={t("ws.imgAlt")}
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700" />
            <p className="absolute bottom-4 left-4 font-mono text-[11px] uppercase tracking-[0.25em] bg-black/70 px-2 py-1">{t("ws.fig")}</p>
          </div>
        </FadeUp>
        <div className="mt-10 border-t border-[var(--line)]">
          {t("ws.services").map(([title, d], i) => (
            <FadeUp key={i} delay={i * 0.08} className="py-6 border-b border-[var(--line)] flex gap-5">
              <span className="font-mono text-xs text-[var(--signal)] pt-1.5">0{i + 1}</span>
              <div>
                <h3 className="font-display text-2xl font-bold uppercase">{title}</h3>
                <p className="text-sm text-[var(--muted)] mt-1 leading-relaxed">{d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>

      <FadeUp className="lg:col-span-7">
        <form data-testid="repair-form" onSubmit={submit} className="clip-cut bg-[var(--ink-2)] border border-[var(--line)] p-6 sm:p-10 space-y-6">
          <div className="flex items-center justify-between gap-4">
            <p className="font-display text-3xl font-black uppercase flex items-center gap-3"><Wrench className="w-6 h-6" />{t("ws.formTitle")}</p>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] hidden sm:block">{t("ws.required")}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((v, i) => (
              <button type="button" key={v} data-testid={`repair-service-${v.toLowerCase()}`} onClick={() => setForm({ ...form, service: v })}
                className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${form.service === v ? "bg-[var(--signal)] border-[var(--signal)] text-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--bone)]"}`}>
                {t("ws.types")[i]}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("f.brand")}><input data-testid="repair-brand-input" required className="field" placeholder={t("ws.brandPh")} value={form.brand} onChange={set("brand")} /></Field>
            <Field label={t("f.model")}><input data-testid="repair-model-input" required className="field" placeholder={t("ws.modelPh")} value={form.model} onChange={set("model")} /></Field>
          </div>
          <Field label={t("f.details")}>
            <textarea data-testid="repair-details-input" required rows={4} className="field resize-none" placeholder={t("ws.detailsPh")} value={form.details} onChange={set("details")} />
          </Field>
          <Field label={t("f.budget")}><input data-testid="repair-budget-input" required className="field" placeholder={t("ws.budgetPh")} value={form.budget} onChange={set("budget")} /></Field>
          <div className="grid sm:grid-cols-3 gap-6">
            <Field label={t("f.name")}><input data-testid="repair-name-input" required className="field" value={form.name} onChange={set("name")} /></Field>
            <Field label={t("f.email")}><input data-testid="repair-email-input" required type="email" className="field" value={form.email} onChange={set("email")} /></Field>
            <label className="block"><span className="field-label">{t("f.phone")}</span>
              <input data-testid="repair-phone-input" className="field" value={form.phone} onChange={set("phone")} /></label>
          </div>
          <button data-testid="repair-submit-button" disabled={busy}
            className="w-full clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-[var(--bone)] transition-colors disabled:opacity-60">
            {busy ? t("inq.sending") : t("ws.send")}
          </button>
        </form>
      </FadeUp>
    </section>
  );
};
